"use client";

import { c as _c } from "react/compiler-runtime";
import { Play } from "lucide-react";
import Image from "next/image";
import { useRef, useState } from "react";
const DURATION_LABEL = "6:35";
interface Chapter {
  at: number;
  time: string;
  label: string;
}
const CHAPTERS: Chapter[] = [{
  at: 0,
  time: "0:00",
  label: "Dashboard & live activity"
}, {
  at: 60,
  time: "1:00",
  label: "Usage by model, key & member"
}, {
  at: 82,
  time: "1:22",
  label: "API keys & IAM rules"
}, {
  at: 132,
  time: "2:12",
  label: "Team roles & per-developer budgets"
}, {
  at: 158,
  time: "2:38",
  label: "Compliance & provider-HQ routing"
}, {
  at: 200,
  time: "3:20",
  label: "Guardrails & security events"
}, {
  at: 240,
  time: "4:00",
  label: "SAML SSO with Microsoft Entra"
}];
export function EnterpriseDemoVideo() {
  const $ = _c(21);
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [activeAt, setActiveAt] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function playFrom(at) {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      setStarted(true);
      setActiveAt(at);
      if (video.readyState === 0) {
        video.addEventListener("loadedmetadata", () => {
          video.currentTime = at;
        }, {
          once: true
        });
      } else {
        video.currentTime = at;
      }
      video.play();
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const playFrom = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = function syncActiveChapter() {
      const video_0 = videoRef.current;
      if (!video_0) {
        return;
      }
      const current = CHAPTERS.reduce((acc, chapter) => video_0.currentTime >= chapter.at ? chapter.at : acc, 0);
      setActiveAt(previous => previous === current ? previous : current);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const syncActiveChapter = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div aria-hidden={true} className="pointer-events-none absolute inset-x-0 -inset-y-6 -z-10 rounded-[2.5rem] bg-[radial-gradient(60%_60%_at_50%_45%,rgba(59,130,246,0.22),transparent_75%)] blur-2xl" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setStarted(true);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <source src="/videos/enterprise-demo.mp4" type="video/mp4" />;
    t5 = <track kind="captions" src="/videos/enterprise-demo.vtt" srcLang="en" label="English" />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] !== started) {
    t6 = <video ref={videoRef} className="h-full w-full" controls={started} controlsList="nodownload" playsInline={true} preload="none" onPlay={t3} onTimeUpdate={syncActiveChapter}>{t4}{t5}</video>;
    t7 = !started && <button type="button" onClick={() => playFrom(0)} aria-label={`Play the enterprise product walkthrough, ${DURATION_LABEL}`} className="group absolute inset-0 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-500"><Image src="/videos/enterprise-demo-poster.jpg" alt="LLM Gateway compliance settings restricting routing by provider headquarters, with 14 allowed and 33 blocked providers" fill={true} loading="eager" sizes="(max-width: 1024px) 100vw, 1024px" className="object-cover" /><span aria-hidden={true} className="absolute inset-0 bg-linear-to-b from-black/85 via-black/25 to-black/45 transition-colors duration-300 group-hover:via-black/15" /><span aria-hidden={true} className="absolute inset-0 flex items-center justify-center"><span className="relative flex size-20 items-center justify-center rounded-full bg-blue-600 text-white shadow-xl shadow-blue-950/50 transition-transform duration-300 ease-out group-hover:scale-110 group-focus-visible:scale-110"><span className="absolute inset-0 rounded-full bg-blue-500/40 motion-safe:animate-ping" /><Play className="relative ml-1 size-8 fill-current" /></span></span><span aria-hidden={true} className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-4 text-left sm:p-6"><span className="flex flex-col"><span className="text-xs font-mono uppercase tracking-wider text-blue-300">Founder walkthrough</span><span className="mt-1 hidden text-sm font-semibold text-white sm:block sm:text-base">The whole enterprise product, unedited — no sales call</span></span><span className="shrink-0 rounded-full border border-white/25 bg-black/50 px-3 py-1 font-mono text-xs text-white backdrop-blur-sm">{DURATION_LABEL}</span></span></button>;
    $[6] = started;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <div className="relative">{t2}<div className="relative overflow-hidden rounded-2xl border-2 border-border/80 bg-card p-1.5 shadow-[0_0_80px_-20px_rgba(59,130,246,0.35)]"><div className="relative aspect-[1592/1000] overflow-hidden rounded-xl bg-muted">{t6}{t7}</div></div></div>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="mb-3 text-center text-xs font-mono uppercase tracking-wider text-muted-foreground">Jump to</p>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== activeAt || $[14] !== started) {
    t10 = CHAPTERS.map(chapter_0 => {
      const isActive = started && activeAt === chapter_0.at;
      return <button key={chapter_0.at} type="button" onClick={() => playFrom(chapter_0.at)} aria-label={`Play ${chapter_0.label}, starting at ${chapter_0.time}`} className={`flex items-center gap-2 rounded-full border px-3 py-2.5 text-sm transition-colors sm:py-1.5 ${isActive ? "border-blue-500/60 bg-blue-500/10 text-foreground" : "border-border bg-card/50 text-muted-foreground hover:border-blue-500/50 hover:text-foreground"}`}><span className="font-mono text-xs text-blue-500">{chapter_0.time}</span>{chapter_0.label}</button>;
    });
    $[13] = activeAt;
    $[14] = started;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10) {
    t11 = <div className="mt-6">{t9}<div className="flex flex-wrap justify-center gap-2">{t10}</div></div>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t8) {
    t12 = <div className="mx-auto mt-16 max-w-5xl">{t8}{t11}</div>;
    $[18] = t11;
    $[19] = t8;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
}